import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client.js';
import { useLogin, useRegister } from '../api/hooks.js';
import { Logo } from './Logo.js';

/**
 * Sign in, or create an account.
 *
 * The session token never appears here: the server sets an httpOnly cookie and
 * this form only ever posts credentials. There is nothing to store and nothing
 * for an XSS bug to read.
 */
export function Login() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [kind, setKind] = useState<'member' | 'creator'>('member');

  const login = useLogin();
  const register = useRegister();
  const pending = login.isPending || register.isPending;
  const error = login.error ?? register.error;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (mode === 'login') login.mutate({ email, password });
    else register.mutate({ email, password, displayName, kind });
  }

  return (
    <main className="auth">
      <div className="auth__card">
        <Logo size={44} title="KickBack Pro" />
        <h1 className="auth__title">KickBack Pro</h1>
        <p className="auth__subtitle">
          {mode === 'login' ? 'Sign in to your account' : 'Create an account'}
        </p>

        <form onSubmit={onSubmit} className="form">
          {mode === 'register' && (
            <>
              <label className="field">
                <span className="field__label">Name</span>
                <input
                  className="field__input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  maxLength={80}
                  autoComplete="name"
                />
              </label>

              <fieldset className="field">
                <legend className="field__label">I am a</legend>
                <div className="toggle">
                  {(['member', 'creator'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`toggle__option${kind === option ? ' toggle__option--on' : ''}`}
                      onClick={() => setKind(option)}
                    >
                      {option === 'member' ? 'Fan' : 'Creator'}
                    </button>
                  ))}
                </div>
              </fieldset>
            </>
          )}

          <label className="field">
            <span className="field__label">Email</span>
            <input
              className="field__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>

          <label className="field">
            <span className="field__label">Password</span>
            <input
              className="field__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={10}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
            {mode === 'register' && (
              <span className="field__hint">At least 10 characters. Length beats punctuation.</span>
            )}
          </label>

          {error !== null && (
            <div className="alert" role="alert">
              {error instanceof ApiError ? error.message : 'Something went wrong.'}
              {error instanceof ApiError && error.requestId !== undefined && (
                <span className="alert__id">Reference {error.requestId}</span>
              )}
            </div>
          )}

          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <button
          className="button button--quiet"
          type="button"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            login.reset();
            register.reset();
          }}
        >
          {mode === 'login' ? 'Create an account instead' : 'I already have an account'}
        </button>
      </div>

      {/*
        The one place a signed-out visitor can reach the legal pages, and the
        reason it is on this screen rather than in the dashboard header: an app
        store reviewer, or someone deciding whether to hand us a card, never
        gets past here. `<a>` rather than a router link because these are static
        files in `public/` — deliberately not part of the bundle, so they render
        with the app broken, the API down, or JavaScript off, which is exactly
        when someone wants to read a privacy policy.
      */}
      <nav className="auth__legal">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Cashback terms</a>
        <a href="/delete-account">Delete your account</a>
      </nav>
    </main>
  );
}
